import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Grass as GrassIcon,
  Spa as SpaIcon,
  TrendingUp as TrendingUpIcon,
  ShowChart as ShowChartIcon,
  Grass,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import GenericButton from '../Custom/GenericButton';
import { getUserBasicDetailFromAuth } from '../../utils/common';
import { getCropGrowthData } from '../../api/amSatelliteReport';
import api from '../../globals/axios';

const CropGrowthGraph = ({ farmId, accessKey }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cropGrowthData, setCropGrowthData] = useState(null);
  const [isCropCreated, setIsCropCreated] = useState(false);
  const [cropGrowthMessage, setCropGrowthMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const { role } = getUserBasicDetailFromAuth();

  useEffect(() => {
    fetchData();
  }, [farmId, accessKey]);

  const fetchData = async () => {
    try {
      let response;
      if (accessKey) {
        response = await getCropGrowthData(farmId, accessKey);
      } else {
        const res = await api.get(
          `/satellitedata/get-crop-growth-insights-v2/${farmId}`
        );
        response = res.data;
      }
      const { is_crop_created, data, message } = response;
      setIsCropCreated(is_crop_created || false);
      setCropGrowthData(data || null);
      setCropGrowthMessage(message || null);
    } catch (err) {
      setCropGrowthData(null);
      setCropGrowthMessage(
        err.response?.data?.message || t('Failed to load crop growth data')
      );
      setIsCropCreated(err.response?.data?.is_crop_created || false);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          my: 4,
          py: 3,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ mb: 2, color: 'primary.main' }}
        />
        <Typography variant="h6" color="text.secondary">
          {t('Loading Crop Growth Data')}...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('This may take a moment')}
        </Typography>
      </Box>
    );
  }

  if (!cropGrowthData) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          p: 4,
          mt: 4,
          border: '1px solid #ccc',
          borderRadius: 2,
          backgroundColor: '#fff',
        }}
      >
        {cropGrowthMessage && (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            {cropGrowthMessage}
          </Typography>
        )}
        {!isCropCreated && role !== 'ADMIN' && (
          <GenericButton
            backgroundColor="primary.main"
            onClick={() => navigate(`/create-crop?farmId=${farmId}`)}
            icon={Grass}
            padding="8px 0"
          >
            {t('Create Crop')}
          </GenericButton>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {cropGrowthData.crop_growth_insights.length > 0 && (
        <>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: '#e9f4d4', height: '100%' }}>
                <CardContent
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    py: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}
                  >
                    <GrassIcon color="success" fontSize="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('Cumulative Growth')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.success.dark,
                    }}
                  >
                    {
                      cropGrowthData.crop_growth_insights[
                        cropGrowthData.crop_growth_insights.length - 1
                      ].crop_growth
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: '#e8f6ff', height: '100%' }}>
                <CardContent
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    py: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}
                  >
                    <SpaIcon color="info" fontSize="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('Current NDVI')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 'bold', color: theme.palette.info.dark }}
                  >
                    {
                      cropGrowthData.crop_growth_insights[
                        cropGrowthData.crop_growth_insights.length - 1
                      ].ndvi_mean
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: '#f9f0e9', height: '100%' }}>
                <CardContent
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    py: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}
                  >
                    <TrendingUpIcon color="secondary" fontSize="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('Average Actual Crop Growth')}
                    <br />({t('since sowing')})
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.secondary.dark,
                    }}
                  >
                    {cropGrowthData.growth_rate}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('kg/ha/day')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Card sx={{ bgcolor: '#e3f4f4', height: '100%' }}>
                <CardContent
                  sx={{
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                    py: 0,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}
                  >
                    <ShowChartIcon color="primary" fontSize="small" />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('Average Attainable Crop Growth')}
                    <br />({t('since sowing')})
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      color: theme.palette.primary.dark,
                    }}
                  >
                    {cropGrowthData.max_growth_rate}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('kg/ha/day')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <ResponsiveContainer
            width="100%"
            height={350}
            style={{ marginTop: 20 }}
          >
            <LineChart
              data={cropGrowthData.crop_growth_insights}
              margin={{ top: 10, right: 30, left: 20, bottom: 50 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                tickMargin={5}
                angle={-45}
                textAnchor="end"
              />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={value => value.toFixed(0)}
                label={{
                  value: 'Crop Growth (kg/ha/day)',
                  angle: -90,
                  position: 'insideBottomLeft',
                  fontSize: 12,
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.90)',
                  borderRadius: '4px',
                }}
                formatter={(value, name) => {
                  if (name === 'Attainable Crop Growth')
                    return [value.toFixed(3), 'Attainable Crop Growth'];
                  if (name === 'Actual Crop Growth')
                    return [value.toFixed(3), 'Actual Crop Growth'];
                  return value;
                }}
                labelFormatter={label => `Date: ${label}`}
              />
              <Legend
                verticalAlign="top"
                height={30}
                iconSize={12}
                wrapperStyle={{
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              />
              <Line
                type="monotone"
                dataKey="max_crop_growth"
                name="Attainable Crop Growth"
                stroke="#1976d2"
                dot={false}
                isAnimationActive={!accessKey}
              />
              <Line
                type="monotone"
                dataKey="crop_growth"
                name="Actual Crop Growth"
                stroke={theme.palette.success.main}
                dot={false}
                isAnimationActive={!accessKey}
              />
            </LineChart>
          </ResponsiveContainer>

          <Card sx={{ mt: 2, mb: 2, bgcolor: '#f8f9fa' }}>
            <CardContent sx={{ py: 2 }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}
              >
                {t('What Does This Chart Show?')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 3,
                        bgcolor: theme.palette.success.main,
                        mr: 2,
                        mt: 1.5,
                        borderRadius: 1,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette.success.dark,
                        }}
                      >
                        {t('How Your Crop is Actually Growing')}
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 'normal',
                            fontSize: '0.8em',
                            color: 'text.secondary',
                          }}
                        >
                          ({t('Green Line')})
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'This shows how fast your crop is really growing right now, based on what satellites can see from space.'
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}
                  >
                    <Box
                      sx={{
                        width: 16,
                        height: 3,
                        bgcolor: '#1976d2',
                        mr: 2,
                        mt: 1.5,
                        borderRadius: 1,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 'bold', color: '#1976d2' }}
                      >
                        {t('How Fast Your Crop Could Grow')}
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 'normal',
                            fontSize: '0.8em',
                            color: 'text.secondary',
                          }}
                        >
                          ({t('Blue Line')})
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'This shows the best possible growth rate your crop could achieve with perfect weather, water, and nutrients.'
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: '#e8f5e8',
                  borderRadius: 1,
                  borderLeft: `4px solid ${theme.palette.success.main}`,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontStyle: 'italic' }}
                >
                  💡 {t('Tip')}:{' '}
                  {t(
                    "When the green line is close to the blue line, your crop is growing very well! If there's a big gap, you might need to improve water, nutrients, or other growing conditions."
                  )}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default CropGrowthGraph;
